import { AxiosResponse, isCancel } from 'axios';

import CustomHttpClient from '../../customHttpClient';

import { getError, getUserInfo } from '../../../utils';

import {
  DeleteBankDepositServiceParams,
  DeleteBankDepositServiceRequestParams,
  DeleteBankDepositServiceResponse,
  DeleteElectronicCheckServiceRequestParams,
  DeleteElectronicCheckServiceResponse,
  DeleteEletronicCheckServiceParams,
  DeleteIntegratedCollectionServiceParams,
  DeleteIntegratedCollectionServiceRequestParams,
  DeleteIntegratedCollectionServiceResponse,
  DeletePreliquidationServiceParams,
  DeletePreliquidationServiceResponse,
  DeleteRetentionServiceParams,
  DeleteRetentionServiceResponse,
  DeleteRoundingServiceParams,
  DeleteRoundingServiceRequestParams,
  DeleteRoundingServiceResponse,
  GenerateNewPreliquidationServiceResponse,
  GetBanksListServiceResponse,
  GetDebtColumnsServiceRequestParams,
  GetDebtColumnsServiceResponse,
  GetPaymentsListServiceParams,
  GetPaymentsListServiceResponse,
  GetPreliquidationPaymentMethodsListServiceResponse,
  GetSavedPreliquidationsServiceResponse,
  GetSentPreliquidationsServiceResponse,
  GetSummaryQuotesRequestParams,
  GetSummaryQuotesServiceParams,
  GetSummaryQuotesServiceResponse,
  IndicatePaymentTypeServiceParams,
  IndicatePaymentTypeServiceRequestParams,
  IndicatePaymentTypeServiceResponse,
  InsertBankDepositServiceParams,
  InsertBankDepositServiceRequestParams,
  InsertBankDepositServiceResponse,
  InsertElectronicCheckServiceRequestParams,
  InsertElectronicCheckServiceResponse,
  InsertEletronicCheckServiceParams,
  InsertIntegratedCollectionServiceParams,
  InsertIntegratedCollectionServiceResponse,
  InsertRetentionServiceParams,
  InsertRetentionServiceResponse,
  InsertRoundingServiceParams,
  InsertRoundingServiceRequestParams,
  InsertRoundingServiceResponse,
  SavePreliquidationServiceParams,
  SavePreliquidationServiceResponse,
  SelectEntireColumnServiceParams,
  SelectEntireColumnServiceRequestParams,
  SelectEntireColumnServiceResponse,
  SelectSuperPolicyEntireColumnServiceParams,
  SelectSuperPolicyEntireColumnServiceRequestParams,
  SelectSuperPolicyEntireColumnServiceResponse,
  SendPreliquidationServiceParams,
  SendPreliquidationServiceResponse,
  UploadPreliquidationDocumentsServiceRequestParams,
  UploadPreliquidationDocumentsServiceResponse,
} from '../../../../pages/collection/PreLiquidation/shared/interfaces';
import { GeneratePreliquidationPDFServiceRequestParams } from '../../../../pages/collection/PreLiquidation/shared/interfaces/services/generate-preliquidation-pdf-request';
import { DocumentUploadServiceRequestParams } from '../../../interfaces/services/post-send-claim-documents-service';

const customHttpClient = CustomHttpClient({
  baseUrl: 'productores/preliquidacion',
});
const customHttpClientProducers = CustomHttpClient({
  baseUrl: 'productores',
});

const PreLiquidationService = {
  async getSummaryQuotes(
    requestParams: GetSummaryQuotesRequestParams
  ): Promise<AxiosResponse<GetSummaryQuotesServiceResponse>> {
    const url = 'obtenerCuotasResumen';
    const params: GetSummaryQuotesServiceParams = {
      ...getUserInfo(),
      ...requestParams,
    };

    try {
      const response =
        await customHttpClient.get<GetSummaryQuotesServiceResponse>(url, {
          params,
        });

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async generateNewPreliquidation(
    includeAutomaticDebit: boolean
  ): Promise<AxiosResponse<GenerateNewPreliquidationServiceResponse, any>> {
    const url = includeAutomaticDebit
      ? 'nuevaPreliquidacionDebito'
      : 'nuevaPreliquidacion';
    const params = { ...getUserInfo() };

    try {
      const response =
        await customHttpClient.post<GenerateNewPreliquidationServiceResponse>(
          url,
          params
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getDebtColumns(
    nroPreliquidation: string
  ): Promise<AxiosResponse<GetDebtColumnsServiceResponse>> {
    const url = 'obtenerCuotas';
    const params: GetDebtColumnsServiceRequestParams = {
      ...getUserInfo(),
      numeroPreliquidacion: nroPreliquidation,
    };

    try {
      const response =
        await customHttpClient.get<GetDebtColumnsServiceResponse>(url, {
          params,
        });

      if (response.status === 200) {
        response.data.cuotas = response.data.cuotas.map((el) => ({
          ...el,
          key: `${el.suplementoSpol}-${el.superpoliza}`,
          deudaAnteriorOPT: false,
          quincenaAnteriorOPT: false,
          quincenaActualOPT: false,
          quincenaSiguienteOPT: false,
          quincenaPosteriorOPT: false,
          saldoOPT: false,
          pagoContadoOPT: false,
        }));
      }

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async selectEntireColumn(
    requestParams: SelectEntireColumnServiceRequestParams
  ): Promise<AxiosResponse<SelectEntireColumnServiceResponse>> {    
    const url = 'marcarDesmarcarColumna';
    const params: SelectEntireColumnServiceParams = {
      ...getUserInfo(),
      ...requestParams,
    };

    try {
      const response =
        await customHttpClient.post<SelectEntireColumnServiceResponse>(
          url,
          params
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async selectSuperPolicyEntireColumn(
    requestParams: SelectSuperPolicyEntireColumnServiceRequestParams
  ): Promise<AxiosResponse<SelectSuperPolicyEntireColumnServiceResponse>> {
    const url = 'marcarDesmarcarColumnaSuperpoliza';

    if (requestParams.suplemento === "Poliza anulada") {
      requestParams.suplemento = "999";
    }

    const params: SelectSuperPolicyEntireColumnServiceParams = {
      ...getUserInfo(),
      ...requestParams,
    };

    

    try {
      const response =
        await customHttpClient.post<SelectSuperPolicyEntireColumnServiceResponse>(
          url,
          params,
          { timeout: 5000 }
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async indicatePaymentType(
    requestParams: IndicatePaymentTypeServiceRequestParams
  ): Promise<AxiosResponse<IndicatePaymentTypeServiceResponse>> {
    const url = 'indicarTipoDePago';
    const params: IndicatePaymentTypeServiceParams = {
      ...getUserInfo(),
      ...requestParams,
    };

    try {
      const response =
        await customHttpClient.post<IndicatePaymentTypeServiceResponse>(
          url,
          params
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async insertElectronicCheck(
    requestParams: InsertElectronicCheckServiceRequestParams
  ): Promise<AxiosResponse<InsertElectronicCheckServiceResponse>> {
    const url = 'chequeElectronico';

    const params: InsertEletronicCheckServiceParams = {
      ...getUserInfo(),
      ...requestParams,
    };

    try {
      const response =
        await customHttpClient.post<InsertElectronicCheckServiceResponse>(
          url,
          params
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async deleteElectronicCheck(
    requestParams: DeleteElectronicCheckServiceRequestParams
  ): Promise<AxiosResponse<DeleteElectronicCheckServiceResponse>> {
    const url = 'chequeElectronico';
    const params: DeleteEletronicCheckServiceParams = {
      ...getUserInfo(),
      ...requestParams,
    };

    try {
      const response =
        await customHttpClient.delete<DeleteElectronicCheckServiceResponse>(
          url,
          { data: params }
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async insertRounding(
    requestParams: InsertRoundingServiceRequestParams
  ): Promise<AxiosResponse<InsertRoundingServiceResponse>> {
    const url = 'redondeo';
    const params: InsertRoundingServiceParams = {
      ...getUserInfo(),
      ...requestParams,
    };

    const bodyParams = {
      base: getUserInfo(),
      accion: "i",
      importe: requestParams.monto,
      archivo: requestParams.archivo,
      observacion: requestParams.observaciones,
      preliquidacion: requestParams.numeroPreliquidacion
    }

    try {
      const response =
        await customHttpClient.post<InsertRoundingServiceResponse>(url, bodyParams);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async deleteRounding(
    requestParams: DeleteRoundingServiceRequestParams
  ): Promise<AxiosResponse<DeleteRoundingServiceResponse>> {
    const url = 'redondeo';

    const bodyParams = {
      base: getUserInfo(),
      accion: "E",
      importe: requestParams.monto,
      archivo: requestParams.archivo,
      observacion: requestParams.observaciones,
      preliquidacion: requestParams.numeroPreliquidacion
    };

    try {
      const response =
        await customHttpClient.delete<DeleteRoundingServiceResponse>(url, {
          data: bodyParams,
        });

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async insertBankDeposit(
    requestParams: InsertBankDepositServiceRequestParams
  ): Promise<AxiosResponse<InsertBankDepositServiceResponse>> {
    const url = 'depositoBancario';

    const params: InsertBankDepositServiceParams = {
      ...getUserInfo(),
      ...requestParams,
    };

    try {
      const response =
        await customHttpClient.post<InsertBankDepositServiceResponse>(
          url,
          params
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async deleteBankDeposit(
    requestParams: DeleteBankDepositServiceRequestParams
  ): Promise<AxiosResponse<DeleteBankDepositServiceResponse>> {
    const url = 'depositoBancario';
    const params: DeleteBankDepositServiceParams = {
      ...getUserInfo(),
      ...requestParams,
    };

    try {
      const response =
        await customHttpClient.delete<DeleteBankDepositServiceResponse>(url, {
          data: params,
        });

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async insertIntegratedCollection(
    requestParams: DeleteIntegratedCollectionServiceParams
  ): Promise<AxiosResponse<InsertIntegratedCollectionServiceResponse>> {
    const url = 'cobranzaIntegrada';
    const params: InsertIntegratedCollectionServiceParams = {
      ...getUserInfo(),
      ...requestParams,
    };

    try {
      const response =
        await customHttpClient.post<InsertIntegratedCollectionServiceResponse>(
          url,
          params
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async deleteIntegratedCollection(
    requestParams: DeleteIntegratedCollectionServiceParams
  ): Promise<AxiosResponse<DeleteIntegratedCollectionServiceResponse>> {
    const url = 'cobranzaIntegrada';
    const params: DeleteIntegratedCollectionServiceParams = {
      ...getUserInfo(),
      ...requestParams,
    };

    try {
      const response =
        await customHttpClient.delete<DeleteIntegratedCollectionServiceResponse>(
          url,
          {
            data: params,
          }
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async insertRetention(
    requestParams: InsertRetentionServiceParams
  ): Promise<AxiosResponse<InsertRetentionServiceResponse>> {
    const url = 'retencion';
    const params: InsertRetentionServiceParams = {
      ...getUserInfo(),
      ...requestParams,
    };

    try {
      const response =
        await customHttpClient.post<InsertRetentionServiceResponse>(
          url,
          params
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async deleteRetention(
    requestParams: DeleteRetentionServiceParams
  ): Promise<AxiosResponse<DeleteRetentionServiceResponse>> {
    const url = 'retencion';


    requestParams.secuencia = requestParams.numeroCheque;
    const params: DeleteRetentionServiceParams = {
      ...getUserInfo(),
      ...requestParams,
    };

    try {
      const response =
        await customHttpClient.delete<DeleteRetentionServiceResponse>(url, {
          data: params,
        });

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getPaymentsList(
    nroPreliquidation: string
  ): Promise<AxiosResponse<GetPaymentsListServiceResponse>> {
    const url = 'listaPagos';
    const params: GetPaymentsListServiceParams = {
      ...getUserInfo(),
      numeroPreliquidacion: nroPreliquidation,
    };

    try {
      const response =
        await customHttpClient.get<GetPaymentsListServiceResponse>(url, {
          params,
        });

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async savePreliquidation(
    nroPreliquidation: string
  ): Promise<AxiosResponse<SavePreliquidationServiceResponse>> {
    const url = 'guardar';
    const params: SavePreliquidationServiceParams = {
      ...getUserInfo(),
      numeroPreliquidacion: nroPreliquidation,
    };

    try {
      const response =
        await customHttpClient.post<SavePreliquidationServiceResponse>(
          url,
          params
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async deletePreliquidation(
    nroPreliquidation: string
  ): Promise<AxiosResponse<DeletePreliquidationServiceResponse>> {
    const url = 'eliminar';
    const params: DeletePreliquidationServiceParams = {
      ...getUserInfo(),
      numeroPreliquidacion: nroPreliquidation,
    };

    try {
      const response =
        await customHttpClient.delete<DeletePreliquidationServiceResponse>(
          url,
          { data: params }
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async sendPreliquidation(
    nroPreliquidation: string
  ): Promise<AxiosResponse<SendPreliquidationServiceResponse>> {
    const url = 'enviar';
    const params: SendPreliquidationServiceParams = {
      ...getUserInfo(),
      numeroPreliquidacion: nroPreliquidation,
    };

    try {
      const response =
        await customHttpClient.post<SendPreliquidationServiceResponse>(
          url,
          params
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getBanksList(): Promise<AxiosResponse<GetBanksListServiceResponse>> {
    const url = 'obtenerBancos';

    try {
      const response = await customHttpClient.get<GetBanksListServiceResponse>(
        url
      );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getPreliquidationPaymentMethodsList(): Promise<
    AxiosResponse<GetPreliquidationPaymentMethodsListServiceResponse>
  > {
    const url = 'obtenerFormasDePago';

    try {
      const response =
        await customHttpClient.get<GetPreliquidationPaymentMethodsListServiceResponse>(
          url
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getSavedPreliquidations(): Promise<
    AxiosResponse<GetSavedPreliquidationsServiceResponse>
  > {
    const url = 'preliquidacionesGuardadas';
    const params = { ...getUserInfo() };

    try {
      const response =
        await customHttpClientProducers.get<GetSavedPreliquidationsServiceResponse>(
          url,
          { params }
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getSentPreliquidations(): Promise<
    AxiosResponse<GetSentPreliquidationsServiceResponse>
  > {
    const url = `preliquidacionesEnviadas`;
    const params = { ...getUserInfo() };

    try {
      const response =
        await customHttpClientProducers.get<GetSentPreliquidationsServiceResponse>(
          url,
          { params }
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async postUploadPreliquidationDocumentation(
    requestParams: DocumentUploadServiceRequestParams
  ): Promise<AxiosResponse<UploadPreliquidationDocumentsServiceResponse>> {
    const url = 'uploadReciboPagoTransferencia';

    try {
      const response =
        await customHttpClientProducers.post<UploadPreliquidationDocumentsServiceResponse>(
          url,
          requestParams
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async generatePreliquidationPdf(
    requestParams: GeneratePreliquidationPDFServiceRequestParams
  ): Promise<AxiosResponse<{base64: string}>> {
    const url = 'documents/certificadoPreliquidacion';

    try {
      const response =
        await customHttpClientProducers.post<{base64: string}>(
          url,
          requestParams
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },
};

export default PreLiquidationService;
